/**
 * Payload for PUT /financial/update-lock-quoted — mirrors the former Quotation "Generate"
 * first-quotation flow (projectsDetails array).
 */

import { scopeSalesHourlyRatesForUser } from "./salespersonRatesScope";

function resolveEntityId(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  return String(value?._id ?? value?.id ?? "").trim();
}

function isInstallationTypeService(typeService) {
  return String(typeService ?? "").toLowerCase().includes("installation");
}

function isHardwareTypeService(typeService) {
  return String(typeService ?? "").toLowerCase().includes("hardware");
}

function getProjectManagerIds(project) {
  const raw = project?.managerid ?? project?.managerId ?? [];
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return list.map(resolveEntityId).filter(Boolean);
}

function getAssignedPersonIds(project) {
  const assigned = project?.Assignedperson ?? project?.AssignedPerson ?? [];
  const list = Array.isArray(assigned) ? assigned : [];
  return list
    .map((p) => resolveEntityId(p?.personid ?? p?.personId ?? p?._id))
    .filter(Boolean);
}

function normalizeDesignation(value) {
  return String(value ?? "").toLowerCase().trim().replace(/\s+/g, " ");
}

function pickManagerRateRow(quotationDetails) {
  return (
    quotationDetails.find((q) => {
      const des = normalizeDesignation(q.designation);
      return (
        des &&
        (des.includes("project manager") ||
          (des.includes("manager") && !des.includes("operations")))
      );
    }) ?? null
  );
}

function pickOperationRateRow(quotationDetails) {
  return (
    quotationDetails.find((q) => {
      const des = normalizeDesignation(q.designation);
      return des && des.includes("operations") && !des.includes("manager");
    }) ?? null
  );
}

function hourlyRateTotal(row) {
  if (!row) return 0;
  const salary =
    Number(row.hourlySalary ?? row.hourlysalaray ?? row.hourly_salary ?? 0) || 0;
  const overhead =
    Number(row.overhead ?? row.overheadpercentage ?? row.overhead_percentage ?? 0) ||
    0;
  return salary + overhead;
}

function buildFromFilteredQuotation(
  filteredQuotation,
  quotationId,
  displayProjectName,
  installRates = null
) {
  if (!filteredQuotation || quotationId == null || String(quotationId).trim() === "") {
    return [];
  }

  const qid = quotationId;
  const projectsDetails = [];

  Object.entries(filteredQuotation).forEach(([projectNameKey, typeServices]) => {
    Object.entries(typeServices || {}).forEach(([typeService, items]) => {
      if (!items?.length) return;
      const item = items[0];
      const quotationDetails = item?.quotationDetails || [];

      const managerQuote = pickManagerRateRow(quotationDetails);
      const operationQuote = pickOperationRateRow(quotationDetails);

      const managerHourlyRate = hourlyRateTotal(managerQuote);
      const operationHourlyRate = hourlyRateTotal(operationQuote);
      const salesmargin =
        Number(item.ProjectMargin ?? item.servicewiseMargin ?? item.Salesmargin ?? 0) || 0;

      const detail = {
        quotationId: qid,
        ProjectName: displayProjectName ?? projectNameKey,
        TypeService: typeService,
        managerHourlyRate,
        operationHourlyRate,
        salesmargin,
      };
      if (isInstallationTypeService(typeService)) {
        detail.perdiem = Number(installRates?.perdiem ?? 0) || 0;
        detail.avgcost =
          Number(installRates?.avgcost ?? installRates?.averagecost ?? 0) || 0;
      }
      projectsDetails.push(detail);
    });
  });

  return projectsDetails;
}

/**
 * Manager + assigned-person rates from hourlyRateQuotation for a project TypeService.
 * quotation.createdBy must match project.managerid; quotation.personid must match
 * project.Assignedperson personid (manager personid is always included).
 */
export function buildManagerAssignedRatesForProject(project, hourlyRateQuotations) {
  if (!project || isHardwareTypeService(project.TypeService)) return [];

  const managerIds = getProjectManagerIds(project);
  if (!managerIds.length) return [];

  const allowedPersonIds = new Set([
    ...managerIds,
    ...getAssignedPersonIds(project),
  ]);
  if (!allowedPersonIds.size) return [];

  const quotes = Array.isArray(hourlyRateQuotations) ? hourlyRateQuotations : [];
  const typeServiceNorm = normalizeDesignation(project.TypeService);
  const results = [];
  const seen = new Set();

  managerIds.forEach((managerId) => {
    const serviceQuotes = quotes.filter((q) => {
      if (resolveEntityId(q.createdBy) !== managerId) return false;
      const personid = resolveEntityId(q.personid);
      if (!personid || !allowedPersonIds.has(personid)) return false;
      const des = normalizeDesignation(q.designation);
      return typeServiceNorm && des.includes(typeServiceNorm);
    });

    serviceQuotes.forEach((q) => {
      const personid = resolveEntityId(q.personid);
      if (!personid) return;

      const dedupeKey = `${personid}:${normalizeDesignation(q.designation)}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);

      results.push({
        designation: q.designation ?? "",
        personid,
        hourlyrate: hourlyRateTotal(q),
      });
    });
  });

  return results;
}

/**
 * Builds the same payload using Redux `projects` + salesperson hourly rates
 * (salesHourlyRate slice), scoped to the logged-in salesperson's createdBy.
 */
export function buildProjectsDetailsPayloadForLeadEdit(
  projects,
  salesHourlyRates,
  quotationId,
  displayProjectName,
  installRates = null,
  loggedUserId = null,
  hourlyRateQuotations = null
) {
  const qid = quotationId != null ? String(quotationId).trim() : "";
  if (!qid || !Array.isArray(projects)) return [];

  const matched = projects.filter(
    (p) =>
      p?.quotationId != null &&
      String(p.quotationId).trim() === qid &&
      p.isProjecttracking === false
  );
  if (matched.length === 0) return [];

  const rates = scopeSalesHourlyRatesForUser(salesHourlyRates, { loggedUserId });
  const typeServices = {};

  matched.forEach((proj) => {
    const typeService = proj.TypeService;
    if (typeService == null || String(typeService).trim() === "") return;
    if (isHardwareTypeService(typeService)) return;

    const typeServiceNorm = normalizeDesignation(typeService);
    const matchedQuotations = rates.filter((q) => {
      const des = normalizeDesignation(q.designation);
      return typeServiceNorm && des && des.includes(typeServiceNorm);
    });

    const item = {
      quotationDetails: matchedQuotations,
      ProjectMargin: proj.Salesmargin,
      servicewiseMargin: proj.servicewiseMargin,
      Salesmargin: proj.Salesmargin,
    };

    if (!typeServices[typeService]) typeServices[typeService] = [];
    typeServices[typeService].push(item);
  });

  const projectKey = qid;
  const filteredQuotation = { [projectKey]: typeServices };
  const projectsDetails = buildFromFilteredQuotation(
    filteredQuotation,
    qid,
    displayProjectName,
    installRates
  );

  return projectsDetails.map((detail) => {
    const proj = matched.find(
      (p) =>
        String(p.TypeService ?? "").trim() === String(detail.TypeService ?? "").trim()
    );
    return {
      ...detail,
      ManagerAssignedRates: proj
        ? buildManagerAssignedRatesForProject(proj, hourlyRateQuotations)
        : [],
    };
  });
}
